import * as Sentry from '@sentry/react';
import { Box, Divider, Flex, Text, Textarea } from '@televet/kibble-ui';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { AppointmentNoteTemplateGlobalId } from '@televet/shared-types/recap/enums/AppointmentNoteTemplateGlobalId';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import {
  FindManyAppointmentNoteTemplateQuery,
  GetAppointmentAiRecapInfoQuery,
  useUpdateOneAppointmentMutation,
} from 'shared/types/graphql';
import { formatToSimpleDateWithTime } from 'shared/utils/formatDate';

const textAreaWrapperStyles = `
  & > div:first-of-type {
    height: 100%;
  }
`;

type NotesTabProps = {
  appointmentData?: GetAppointmentAiRecapInfoQuery['findUniqueAppointment'] | null;
  appointmentNoteTemplateData: NonNullable<FindManyAppointmentNoteTemplateQuery['findManyAppointmentNoteTemplate']>;
  latestRecording?: GetAppointmentAiRecapInfoQuery['findManyRecap'][0];
};

const NotesTab = ({ appointmentData, appointmentNoteTemplateData, latestRecording }: NotesTabProps): JSX.Element => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftNotes, setDraftNotes] = useState<string>('');
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const appointmentId = appointmentData?.id || '';

  const { clinicUserId } = useClinicUser();
  const { isMobile } = useResolutionProvider();
  const toast = useToast();

  const [updateAppointment, { loading: isUpdating }] = useUpdateOneAppointmentMutation({
    onCompleted: () => {
      toast({
        title: 'Notes updated successfully',
        status: 'success',
      });
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast({
        title: 'Failed to update notes',
        status: 'error',
      });
    },
  });

  const recapNotesGeneratedAt = useMemo(() => {
    if (!latestRecording) return null;
    return latestRecording?.createdAt || null;
  }, [latestRecording]);

  const onEdit = useCallback(async (): Promise<void> => {
    if (!isEditing) return setIsEditing(true);

    if (draftNotes !== appointmentData?.recapNotes) {
      return await updateAppointment({
        variables: {
          where: { id: appointmentId },
          data: {
            recapNotes: draftNotes,
            recapNotesUpdatedAt: new Date(),
            recapNotesUpdatedBy: {
              connect: { id: clinicUserId },
            },
          },
        },
      }).then(() => {
        setIsEditing(false);
      });
      // No catch block here, as the onError handler in the mutation will handle errors.
    } else {
      setIsEditing(false);
    }
  }, [appointmentData?.recapNotes, appointmentId, clinicUserId, draftNotes, isEditing, updateAppointment]);

  const onCancel = useCallback((): void => {
    setIsEditing(false);
    setDraftNotes(appointmentData?.recapNotes || '');
  }, [appointmentData?.recapNotes]);

  const onCopy = useCallback((): void => {
    navigator.clipboard.writeText(appointmentData?.recapNotes || '').then(
      () => {
        toast({
          title: 'Your notes have been successfully copied to the clipboard',
          status: 'success',
        });
      },
      (e) => {
        console.warn(e);
        toast({
          title: 'Your notes were not able to be copied to the clipboard.',
          status: 'error',
        });
      },
    );
  }, [appointmentData, toast]);

  useEffect(() => {
    if (appointmentData?.recapNotes) {
      setDraftNotes(appointmentData.recapNotes);
    }
  }, [appointmentData?.recapNotes]);

  const templateType = useMemo(() => {
    // If no recording is available, return a default template message
    if (!latestRecording) {
      return 'Template: None';
    }

    // Check for predefined template types and return the corresponding label
    if (latestRecording.templateId === AppointmentNoteTemplateGlobalId.SOAP) {
      return 'Template: SOAP Notes';
    }
    if (latestRecording.templateId === AppointmentNoteTemplateGlobalId.SUMMARY) {
      return 'Template: Summary Notes';
    }
    if (latestRecording.templateId === AppointmentNoteTemplateGlobalId.NEW) {
      return 'Template: New Notes';
    }

    // If no predefined template matches, look for a custom template in the provided data
    const otherTemplate = appointmentNoteTemplateData?.find((template) => template.id === latestRecording.templateId);

    // Return the custom template name if found, otherwise default to 'None'
    return otherTemplate ? `Template: ${otherTemplate.name}` : 'Template: None';
  }, [appointmentNoteTemplateData, latestRecording]);

  useEffect(() => {
    if (isEditing && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isEditing]);

  // Disable editing on mobile devices since editing is only supported on larger screens.
  useEffect(() => {
    if (isMobile && isEditing) {
      setIsEditing(false);
    }
  }, [draftNotes, isEditing, isMobile]);

  return (
    <>
      {!isMobile && (
        <Flex justifyContent="space-between" mb={5} direction={['column', 'column', 'row', 'row', 'row']}>
          <Box>
            <Text fontWeight="bold" size="md" as="p" mb={1}>
              Summary
            </Text>
            <Text as="p" fontSize="sm" color="text.subtle">
              {appointmentData?.recapNotesUpdatedAt
                ? 'Last edited ' + formatToSimpleDateWithTime(appointmentData.recapNotesUpdatedAt)
                : recapNotesGeneratedAt
                ? 'Generated ' + formatToSimpleDateWithTime(recapNotesGeneratedAt)
                : ''}
            </Text>
          </Box>
          <Flex gap={2} mt={[4, 4, 0, 0, 0]}>
            <Button
              size="md"
              variant="tertiary"
              onClick={isEditing ? onCancel : onCopy}
              width={['100%', '100%', 'auto', 'auto', 'auto']}
              disabled={(isEditing && isUpdating) || !appointmentData?.recapNotes}
            >
              {isEditing ? 'Cancel' : 'Copy Notes'}
            </Button>
            <Button
              size="md"
              variant={isEditing ? 'primary' : 'tertiary'}
              iconName={isEditing ? 'checkmark' : 'pen'}
              onClick={onEdit}
              isLoading={isUpdating}
              width={['100%', '100%', 'auto', 'auto', 'auto']}
            >
              {isEditing ? 'Done Editing' : 'Edit Notes'}
            </Button>
          </Flex>
        </Flex>
      )}
      <Flex
        direction="column"
        height="100%"
        background={isEditing ? undefined : 'background.subtle'}
        borderRadius="md"
        py={isMobile ? 2 : isEditing ? undefined : 5}
        overflow="visible"
      >
        {!isEditing && (
          <Box px={isMobile ? 2 : 5}>
            <Text as="p" size="md" fontWeight="bold" mb={3}>
              {templateType}
            </Text>
            <Divider borderColor="text.success" />
          </Box>
        )}
        {isUpdating && (
          <Alert status="warning" title="Saving changes..." hideCloseButton mx={0} mb={5} overflow="visible" />
        )}
        <Textarea
          {...(isMobile
            ? {
                p: 2,
              }
            : isEditing
            ? {
                py: 4,
                px: 5,
              }
            : { ml: 4, pl: 1, mr: 5, mt: 3 })}
          value={draftNotes}
          ref={textAreaRef}
          onChange={(e): void => setDraftNotes(e.target.value)}
          overflow="auto !important"
          formControlStyle={{
            height: '100%',
            css: textAreaWrapperStyles,
          }}
          height="100%"
          resize="none"
          {...(!isEditing && {
            isReadOnly: true,
            background: 'transparent',
            outline: 'none',
            borderColor: 'transparent !important',
            _focus: { boxShadow: 'none ' },
          })}
        />
      </Flex>
    </>
  );
};

export default NotesTab;
